// Presentación de cada nivel de insignia de racha (degradado + icono). Se resuelve por POSICIÓN
// en el catálogo que sirve el backend (ver StreakWidget.tsx), nunca por nombre ni por código: el
// catálogo es editable desde Gestión y un fallback por nombre se rompería al renombrar.
// Extraído de StreakWidget.tsx para que BadgeCelebration use exactamente la misma resolución — dos
// copias de este array podrían divergir en silencio.
//
// D-S1-9 (2026-08-06, reversa parcial de D-S1-6 a pedido explícito) — `skinFor` SÍ deriva el
// degradado del `color` real de Gestión, y `BadgeIcon` SÍ pinta el `iconUrl` cuando existe. D-S1-6
// evitaba ambos porque el color de brandbook (`#342F82`, `#F08521`…) aplicado plano dejaba
// `--bg1 === --bg2` (círculo sin degradado) y "Primer Destello" casi invisible sobre el fondo del
// widget (`#332f81`, casi idéntico). `skinFor` resuelve las dos cosas sin abandonar el color real:
// aclara el mismo tono en dos paradas (15 % y 45 % hacia blanco) para obtener degradado y contraste
// garantizado contra el fondo oscuro. Cuando el catálogo no trae `color` (o no es un hex válido),
// sigue cayendo en `BADGE_SKINS` por índice, como antes.
import { StreakStar } from './StreakTracker';

export interface BadgeSkin {
  emoji?: string; // icono literal para los niveles que NO son la estrella
  g1: string;
  g2: string;
}

export const BADGE_SKINS: BadgeSkin[] = [
  { emoji: '✨', g1: '#f59e0b', g2: '#fcd34d' },
  { g1: '#8b5cf6', g2: '#e879f9' }, // estrella → SVG, no emoji
  { emoji: '🌟', g1: '#f97316', g2: '#fbbf24' },
  { emoji: '🌌', g1: '#6366f1', g2: '#ec4899' },
  { emoji: '💫', g1: '#06b6d4', g2: '#a78bfa' },
];

export function skinAt(index: number): BadgeSkin {
  return BADGE_SKINS[index % BADGE_SKINS.length];
}

const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;

/** Aclara un color hex hacia blanco en la proporción dada (0 = sin cambio, 1 = blanco puro). */
function lighten(hex: string, ratio: number): string {
  const value = parseInt(hex.slice(1), 16);
  const channel = (shift: number) => {
    const base = (value >> shift) & 0xff;
    return Math.round(base + ratio * (255 - base)).toString(16).padStart(2, '0');
  };
  return `#${channel(16)}${channel(8)}${channel(0)}`;
}

/**
 * Degradado de una insignia concreta: si trae `color` real y es un hex válido, lo aclara en dos
 * paradas (ver D-S1-9) para garantizar degradado y contraste; si no, cae en la presentación por
 * índice de `BADGE_SKINS`. El emoji de respaldo (para cuando la insignia no trae `iconUrl`) siempre
 * sale de `BADGE_SKINS`, nunca se inventa uno nuevo por color.
 */
export function skinFor(badge: Readonly<{ color?: string | null }>, index: number): BadgeSkin {
  const fallback = skinAt(index);
  const color = badge.color;
  if (!color || !HEX_COLOR_RE.test(color)) return fallback;
  return { emoji: fallback.emoji, g1: lighten(color, 0.15), g2: lighten(color, 0.45) };
}

/**
 * Icono de una insignia: candado si está pendiente; si está ganada, el `iconUrl` configurado en
 * Gestión cuando existe (D-S1-9) y, si no, la presentación del NIVEL —su emoji o, para el segundo,
 * la estrella dibujada—.
 */
export function BadgeIcon(
  { skin, earned, iconUrl }: Readonly<{ skin: BadgeSkin; earned: boolean; iconUrl?: string | null }>,
) {
  if (!earned) return <span className="badge-icon" aria-hidden="true">🔒</span>;
  if (iconUrl) return <img src={iconUrl} alt="" className="badge-icon-img" />;
  if (skin.emoji) return <span className="badge-icon" aria-hidden="true">{skin.emoji}</span>;
  return <StreakStar color={skin.g1} light={skin.g2} />;
}
